import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Alert, CircularProgress, Snackbar, useTheme,
} from '@mui/material';
import {
  FactCheckOutlined as ApprovalsIcon, CheckRounded as ApproveIcon, ReplyRounded as RejectIcon,
  HistoryRounded as HistoryIcon, InboxOutlined as InboxIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import { SoftCard, SoftTitle, Pill, EmptyState } from '../components/dashboard/Soft';
import { captionSx, fmtHours, dayLabel, tint, relativeTime, initials } from '../components/dashboard/dashboardUtils';

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_FILL = { pending: 'lemon', approved: 'mint', rejected: 'rose', changed: 'peach' };
const STATUS_LABEL = { pending: 'Waiting', approved: 'Approved', rejected: 'Sent back', changed: 'Changed since approval' };

/** One submission in the left-hand list. */
const SubmissionRow = ({ s, active, onClick }) => {
  const theme = useTheme();
  const t = tint(theme, STATUS_FILL[s.status] || 'slate');
  const av = tint(theme, ['lavender', 'sky', 'mint', 'peach', 'rose'][s.userId % 5]);
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 3, px: 3, py: 2.5, borderRadius: 3, cursor: 'pointer',
        bgcolor: active ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6') : 'transparent',
        '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f6f7f9' },
      }}
    >
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: av.ink, bgcolor: av.bg }}>
        {initials(s.user?.name)}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{s.user?.name}</Typography>
        <Typography noWrap sx={{ ...captionSx, fontSize: '0.75rem' }}>{s.label} · {fmtHours(s.totalHours)} h</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Pill fill={STATUS_FILL[s.status] || 'slate'} dot={false}>{STATUS_LABEL[s.status] || s.status}</Pill>
        <Typography sx={{ ...captionSx, fontSize: '0.7rem', mt: 0.5, color: t.ink }}>{relativeTime(s.status === 'pending' ? s.submittedAt : s.updatedAt)}</Typography>
      </Box>
    </Box>
  );
};

const Approvals = () => {
  const theme = useTheme();
  const [params, setParams] = useSearchParams();
  const [queue, setQueue] = useState({ pending: [], recent: [] });
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [deciding, setDeciding] = useState(false);

  const selectedId = params.get('submission');

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const r = await fetch(`${API}/timesheet-approvals/pending`, { headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json()).message || 'Failed to load approvals');
      setQueue(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Open the first pending one by default (so an email link or a fresh visit both land somewhere useful).
  useEffect(() => {
    if (!selectedId && queue.pending.length) setParams({ submission: String(queue.pending[0].id) }, { replace: true });
  }, [queue.pending, selectedId, setParams]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return undefined; }
    let cancelled = false;
    setLoadingDetail(true);
    fetch(`${API}/timesheet-approvals/${selectedId}`, { headers: authHeaders() })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).message || 'Failed to load timesheet'); return r.json(); })
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) { setError(e.message); setDetail(null); } })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const decide = async (action) => {
    if (!detail) return;
    setDeciding(true);
    setError('');
    try {
      const r = await fetch(`${API}/timesheet-approvals/${detail.submission.id}/decide`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ action, comment }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Failed to save decision');
      setToast(action === 'approve' ? `Approved ${detail.submission.user?.name}'s timesheet.` : `Sent back to ${detail.submission.user?.name}.`);
      setRejectOpen(false);
      setComment('');
      setDetail((d) => ({ ...d, submission: data.submission, canDecide: false }));
      loadQueue();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeciding(false);
    }
  };

  // Entries grouped by day for the detail table.
  const byDay = useMemo(() => {
    if (!detail) return [];
    const groups = new Map(detail.days.map((d) => [d.date, { ...d, entries: [] }]));
    detail.entries.forEach((e) => { const g = groups.get(String(e.date).slice(0, 10)); if (g) g.entries.push(e); });
    return [...groups.values()];
  }, [detail]);

  const sub = detail?.submission;
  const hasScan = detail?.hasEmployeeId;
  const over = (d) => d.scanned && !d.scanned.missingOut && d.scanned.scannedHours > 0 && d.hours > d.scanned.scannedHours;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#17181c' : '#fafbfc', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <PageHeader title="Timesheet approvals" />
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 7fr)' }, gap: 4, alignItems: 'start' }}>
          {/* Queue */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <SoftCard>
              <SoftTitle icon={InboxIcon} fill="lemon" title="Waiting for you" sub={queue.pending.length ? `${queue.pending.length} to review, oldest first` : 'Nothing waiting'} />
              {loadingQueue ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : queue.pending.length === 0 ? (
                <EmptyState icon={ApprovalsIcon} fill="mint" title="All caught up" sub="Timesheets your people submit show up here, and you get an email for each one." />
              ) : queue.pending.map((s) => (
                <SubmissionRow key={s.id} s={s} active={String(s.id) === selectedId} onClick={() => setParams({ submission: String(s.id) })} />
              ))}
            </SoftCard>
            {queue.recent.length > 0 && (
              <SoftCard>
                <SoftTitle icon={HistoryIcon} fill="slate" title="Recent" sub="Decided in the last 30 days" />
                {queue.recent.map((s) => (
                  <SubmissionRow key={s.id} s={s} active={String(s.id) === selectedId} onClick={() => setParams({ submission: String(s.id) })} />
                ))}
              </SoftCard>
            )}
          </Box>

          {/* Detail */}
          <SoftCard sx={{ minHeight: 320 }}>
            {loadingDetail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
            ) : !sub ? (
              <EmptyState icon={ApprovalsIcon} fill="slate" title="Pick a timesheet" sub="Choose one on the left to see the hours by day and task." />
            ) : (
              <>
                <SoftTitle
                  icon={ApprovalsIcon} fill={STATUS_FILL[sub.status] || 'slate'}
                  title={`${sub.user?.name} · ${sub.label}`}
                  sub={`${fmtHours(sub.totalHours)} h · ${sub.entryCount} entr${sub.entryCount === 1 ? 'y' : 'ies'} · submitted ${relativeTime(sub.submittedAt)}${sub.version > 1 ? ` · resubmitted ${sub.version - 1}×` : ''}`}
                  right={<Pill fill={STATUS_FILL[sub.status] || 'slate'}>{STATUS_LABEL[sub.status] || sub.status}</Pill>}
                />
                {sub.status !== 'pending' && sub.decider && (
                  <Alert severity={sub.status === 'approved' ? 'success' : 'warning'} sx={{ mb: 3 }}>
                    {sub.status === 'approved' ? 'Approved' : 'Sent back'} by {sub.decider.name} {relativeTime(sub.decidedAt)}{sub.comment ? `: "${sub.comment}"` : ''}.
                    {sub.status === 'changed' ? ' Hours were changed afterwards, so it needs to be submitted again.' : ''}
                  </Alert>
                )}
                {detail.currentTotalHours !== sub.totalHours && sub.status === 'pending' && (
                  <Alert severity="info" sx={{ mb: 3 }}>Hours have changed since submission: now {fmtHours(detail.currentTotalHours)} h. Approving records the current hours.</Alert>
                )}
                <Table size="small" sx={{ '& td, & th': { borderBottom: `1px solid ${theme.palette.divider}`, px: 1 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...captionSx, fontWeight: 700 }}>Day</TableCell>
                      <TableCell sx={{ ...captionSx, fontWeight: 700 }}>Task</TableCell>
                      <TableCell align="right" sx={{ ...captionSx, fontWeight: 700 }}>Hours</TableCell>
                      {hasScan && <TableCell align="right" sx={{ ...captionSx, fontWeight: 700 }}>Scanned</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byDay.map((d) => {
                      const weekend = [0, 6].includes(new Date(`${d.date}T00:00:00`).getDay());
                      if (d.entries.length === 0 && (weekend || sub.periodType === 'month')) return null;
                      const scanText = d.scanned ? `${fmtHours(d.scanned.scannedHours)} h${d.scanned.missingOut ? ' (no out)' : ''}` : '—';
                      return (
                        <React.Fragment key={d.date}>
                          <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{dayLabel(d.date, { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                            <TableCell sx={{ ...captionSx, fontSize: '0.75rem' }}>{d.entries.length === 0 ? 'No hours logged' : ''}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums', color: over(d) ? 'error.main' : undefined }}>{d.hours ? `${fmtHours(d.hours)} h` : ''}</TableCell>
                            {hasScan && <TableCell align="right" sx={{ ...captionSx, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>{scanText}</TableCell>}
                          </TableRow>
                          {d.entries.map((e) => (
                            <TableRow key={e.id}>
                              <TableCell />
                              <TableCell sx={{ fontSize: '0.8125rem' }}>
                                <Box component="span" sx={{ fontWeight: 500 }}>{e.taskTitle}</Box>
                                <Typography component="span" sx={{ ...captionSx, fontSize: '0.72rem', ml: 1 }}>{e.projectKey || e.projectName}{e.fileName ? ` · ${e.fileName}` : ''}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}>{fmtHours(e.hours)}</TableCell>
                              {hasScan && <TableCell />}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {hasScan && <Typography sx={{ ...captionSx, fontSize: '0.72rem', mt: 2 }}>Scanned = first to last biometric punch that day. A red total means more hours were logged than scanned.</Typography>}
                {detail.canDecide && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="inherit" startIcon={<RejectIcon />} onClick={() => { setComment(''); setRejectOpen(true); }} disabled={deciding} sx={{ borderRadius: 999, fontWeight: 700 }}>Send back</Button>
                    <Button variant="contained" disableElevation startIcon={<ApproveIcon />} onClick={() => decide('approve')} disabled={deciding} sx={{ borderRadius: 999, fontWeight: 700, px: 4 }}>
                      {deciding ? 'Saving…' : 'Approve'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </SoftCard>
        </Box>
      </Box>

      <Dialog open={rejectOpen} onClose={() => !deciding && setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send back to {sub?.user?.name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...captionSx, mb: 2 }}>They get an email with your note and can edit and resubmit.</Typography>
          <TextField autoFocus fullWidth multiline minRows={3} label="What needs to change?" value={comment} onChange={(e) => setComment(e.target.value)} inputProps={{ maxLength: 500 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={deciding}>Cancel</Button>
          <Button variant="contained" color="warning" disableElevation onClick={() => decide('reject')} disabled={deciding || !comment.trim()}>Send back</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default Approvals;
